/**
 * HTML Serializer — Converts builder state back to HTML source
 *
 * Takes FormBuilderState and generates the canonical HTML representation
 * of the form. This is the reverse of htmlParser.ts and ensures
 * bidirectional synchronization between No-Code and Developer views.
 */

import {
    BuilderFieldDef,
    FormBuilderState,
} from "../types/formBuilder.types";
import { getFieldTag, resolveFieldType } from "../components/shared/FieldTypeRegistry";

const INDENT = "  ";
const FORM_ATTRS = [
    "prm:translation-id",
    "prm:target-table",
    "prm:on-submit",
    "prm:success-message",
    "prm:success-redirect",
    "prm:record-id",
    "prm:events",
];
const FIELD_ATTRS = [
    "prm:uid",
    "name",
    "label",
    "required",
    "placeholder",
    "minlength",
    "maxlength",
    "pattern",
    "prm:currency-code",
    "prm:currency-field",
    "accept",
    "prm:variant",
    "multiple",
    "min",
    "max",
    "options",
    "prm:map",
    "prm:source",
    "prm:display",
    "prm:reference-filter",
    "prm:default",
    "prm:rule:visible",
    "prm:rule:required",
    "prm:rule:readonly",
    "prm:rule:hidden",
    "prm:rule:disabled",
    "prm:rule:label",
    "class",
    "style",
];
const VOID_TAGS = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"]);

/**
 * Serialize form builder state into HTML string.
 */
export const serializeFormHtml = (state: FormBuilderState): string => {
    const mergedHtml = serializeMergedFormHtml(state);
    if (mergedHtml) return mergedHtml;

    return serializeCanonicalFormHtml(state);
};

/**
 * Serialize form builder state into the canonical HTML representation.
 */
const serializeCanonicalFormHtml = (state: FormBuilderState): string => {
    const lines: string[] = [];

    // ── <prm-form> opening ──
    const formAttrs = buildFormAttrs(state).map(([name, value]) => `${name}="${esc(value)}"`);

    const formAttrStr = formAttrs.length > 0 ? "\n" + formAttrs.map((a) => `${INDENT}${a}`).join("\n") + "\n" : "";
    lines.push(`<prm-form${formAttrStr}>`);

    const isMultiStep = state.steps.length > 0;

    if (isMultiStep) {
        // ── Multi-step layout ──
        for (const step of state.steps) {
            lines.push("");
            const stepAttrs: string[] = [];
            if (step.title) stepAttrs.push(`title="${esc(step.title)}"`);
            if (step.icon) stepAttrs.push(`icon="${esc(step.icon)}"`);
            lines.push(`${INDENT}<prm-step ${stepAttrs.join(" ")}>`);

            const stepFields = state.fields.filter((f) => step.fields.includes(f.name));
            lines.push(...serializeFieldGroup(stepFields, state, 2));

            lines.push(`${INDENT}</prm-step>`);
        }
    } else {
        // ── Single-page layout ──
        lines.push(...serializeFieldGroup(state.fields, state, 1));
    }

    // ── Submit button ──
    lines.push("");
    lines.push(`${INDENT}<prm-submit label="Submit" />`);

    lines.push("</prm-form>");

    return lines.join("\n");
};


/**
 * Merge No-Code changes into the existing Developer HTML so custom nodes and
 * custom attributes survive round-trips between both views.
 */
const serializeMergedFormHtml = (state: FormBuilderState): string | null => {
    if (typeof DOMParser === "undefined" || !state.htmlSource?.trim()) return null;

    const parser = new DOMParser();
    const doc = parser.parseFromString(wrapInBody(normalizePrmSelfClosingTags(state.htmlSource)), "text/html");
    const formEl = doc.querySelector("prm-form");
    if (!formEl) return null;

    syncFormAttrs(formEl, state);
    const stepEls = syncStepElements(formEl, state);
    const fieldsByName = collectFieldElements(formEl);
    const stateFields = new Set(state.fields.map((field) => field.name));
    const orphanedFields = Array.from(fieldsByName.entries())
        .filter(([name]) => !stateFields.has(name))
        .map(([, el]) => el);

    state.fields.forEach((field) => {
        const expectedTag = getFieldTag(field.type);
        const existing = fieldsByName.get(field.name);
        const host = getFieldHost(formEl, stepEls, field);

        if (existing && existing.tagName.toLowerCase() === expectedTag) {
            syncFieldAttrs(existing, field);
            syncChoiceItems(existing, field);
            moveFieldElement(host, existing);
            return;
        }

        const orphanIndex = orphanedFields.findIndex((el) => el.tagName.toLowerCase() === expectedTag);
        if (!existing && orphanIndex >= 0) {
            const reusable = orphanedFields.splice(orphanIndex, 1)[0];
            syncFieldAttrs(reusable, field);
            syncChoiceItems(reusable, field);
            moveFieldElement(host, reusable);
            return;
        }

        const nextEl = createFieldElement(doc, field);
        if (existing) {
            copyCustomAttrs(existing, nextEl, FIELD_ATTRS);
            existing.replaceWith(nextEl);
            moveFieldElement(host, nextEl);
            return;
        }

        moveFieldElement(host, nextEl);
    });

    orphanedFields.forEach((el) => el.remove());
    ensureSubmitElement(doc, formEl);
    reorderFieldElements(formEl, stepEls, state);

    return serializeNode(formEl, 0);
};

const syncFormAttrs = (formEl: Element, state: FormBuilderState): void => {
    FORM_ATTRS.forEach((attr) => formEl.removeAttribute(attr));
    buildFormAttrs(state).forEach(([name, value]) => formEl.setAttribute(name, value));
};

const buildFormAttrs = (state: FormBuilderState): Array<[string, string]> => {
    const attrs: Array<[string, string]> = [];
    if (state.translationId) attrs.push(["prm:translation-id", state.translationId]);
    if (state.targetTable) attrs.push(["prm:target-table", state.targetTable]);
    if (state.onSubmit && state.onSubmit !== "create") attrs.push(["prm:on-submit", state.onSubmit]);
    if (state.successMessage) attrs.push(["prm:success-message", state.successMessage]);
    if (state.successRedirect) attrs.push(["prm:success-redirect", state.successRedirect]);
    if (state.recordId) attrs.push(["prm:record-id", state.recordId]);
    if (state.eventsBinding) attrs.push(["prm:events", state.eventsBinding]);
    return attrs;
};

const syncStepElements = (formEl: Element, state: FormBuilderState): Element[] => {
    if (!state.steps.length) return [];

    const doc = formEl.ownerDocument;
    const submitEl = findDirectSubmit(formEl);
    const currentSteps = Array.from(formEl.children).filter((child) => child.tagName.toLowerCase() === "prm-step");

    state.steps.forEach((step, index) => {
        const stepEl = currentSteps[index] || doc.createElement("prm-step");
        stepEl.setAttribute("title", step.title || `Step ${index + 1}`);
        if (step.icon) stepEl.setAttribute("icon", step.icon);
        else stepEl.removeAttribute("icon");
        if (!currentSteps[index]) formEl.insertBefore(stepEl, submitEl);
    });

    currentSteps.slice(state.steps.length).forEach((stepEl) => stepEl.remove());

    return Array.from(formEl.children).filter((child) => child.tagName.toLowerCase() === "prm-step");
};

const collectFieldElements = (formEl: Element): Map<string, Element> => {
    const fields = new Map<string, Element>();
    Array.from(formEl.querySelectorAll("[name]")).forEach((el) => {
        const name = el.getAttribute("name");
        if (!name || fields.has(name)) return;
        if (!resolveFieldType(el.tagName.toLowerCase())) return;
        fields.set(name, el);
    });
    return fields;
};

const isBuilderFieldElement = (el: Element): boolean => Boolean(resolveFieldType(el.tagName.toLowerCase()));

const reorderFieldElements = (formEl: Element, stepEls: Element[], state: FormBuilderState): void => {
    const fieldsByName = collectFieldElements(formEl);
    const fieldsByHost = new Map<Element, BuilderFieldDef[]>();

    state.fields.forEach((field) => {
        const host = getFieldHost(formEl, stepEls, field);
        const hostFields = fieldsByHost.get(host) || [];
        hostFields.push(field);
        fieldsByHost.set(host, hostFields);
    });

    fieldsByHost.forEach((fields, host) => reorderHostFields(host, fields, fieldsByName));
};

const reorderHostFields = (host: Element, fields: BuilderFieldDef[], fieldsByName: Map<string, Element>): void => {
    const currentSlots = Array.from(host.children).filter(isBuilderFieldElement);
    if (!currentSlots.length) return;

    const desiredFields = fields
        .map((field) => fieldsByName.get(field.name))
        .filter((fieldEl): fieldEl is Element => Boolean(fieldEl && fieldEl.parentElement === host));
    if (!desiredFields.length) return;

    const markers = currentSlots.map((slot) => {
        const marker = host.ownerDocument.createComment("prm-field-slot");
        host.insertBefore(marker, slot);
        return marker;
    });

    desiredFields.forEach((fieldEl, index) => {
        const marker = markers[index];
        if (marker) {
            host.insertBefore(fieldEl, marker);
            marker.remove();
            return;
        }

        const submitEl = findDirectSubmit(host);
        host.insertBefore(fieldEl, submitEl);
    });

    markers.slice(desiredFields.length).forEach((marker) => marker.remove());
};

const getFieldHost = (formEl: Element, stepEls: Element[], field: BuilderFieldDef): Element => {
    if (typeof field.stepIndex === "number" && stepEls[field.stepIndex]) {
        return stepEls[field.stepIndex];
    }
    return formEl;
};

const moveFieldElement = (host: Element, fieldEl: Element): void => {
    if (fieldEl.parentElement === host) return;

    const submitEl = findDirectSubmit(host);
    host.insertBefore(fieldEl, submitEl);
};

const findDirectSubmit = (host: Element): Element | null =>
    Array.from(host.children).find((child) => child.tagName.toLowerCase() === "prm-submit") || null;

const ensureSubmitElement = (doc: Document, formEl: Element): void => {
    if (findDirectSubmit(formEl)) return;
    const submitEl = doc.createElement("prm-submit");
    submitEl.setAttribute("label", "Submit");
    formEl.appendChild(submitEl);
};

const createFieldElement = (doc: Document, field: BuilderFieldDef): Element => {
    const template = doc.createElement("template");
    template.innerHTML = serializeField(field, "").trim();
    return template.content.firstElementChild || doc.createElement(getFieldTag(field.type));
};

const syncFieldAttrs = (el: Element, field: BuilderFieldDef): void => {
    FIELD_ATTRS.forEach((attr) => el.removeAttribute(attr));
    buildFieldAttrs(field).forEach(([name, value]) => el.setAttribute(name, value));
};

const copyCustomAttrs = (from: Element, to: Element, controlledAttrs: string[]): void => {
    const controlled = new Set(controlledAttrs);
    Array.from(from.attributes).forEach((attr) => {
        if (controlled.has(attr.name.toLowerCase())) return;
        to.setAttribute(attr.name, attr.value);
    });
};

const syncChoiceItems = (el: Element, field: BuilderFieldDef): void => {
    if (field.type !== "choice") return;

    Array.from(el.children).forEach((child) => {
        if (child.tagName.toLowerCase() === "prm-choice-field-item") child.remove();
    });

    (field.choiceItems || []).forEach((item) => {
        const itemEl = el.ownerDocument.createElement("prm-choice-field-item");
        itemEl.setAttribute("label", item.label);
        itemEl.setAttribute("value", item.value);
        el.appendChild(itemEl);
    });
};

/**
 * Serialize a group of fields, handling sections and rows.
 */
const serializeFieldGroup = (
    fields: BuilderFieldDef[],
    state: FormBuilderState,
    depth: number
): string[] => {
    const lines: string[] = [];
    const indent = INDENT.repeat(depth);
    let currentSection: string | null = null;

    for (const field of fields) {
        // Handle section transitions
        if (field.sectionTitle && field.sectionTitle !== currentSection) {
            if (currentSection !== null) {
                lines.push(`${indent}</prm-section>`);
            }
            const section = state.sections.find((s) => s.title === field.sectionTitle);
            const sectionAttrs = [`title="${esc(field.sectionTitle)}"`];
            if (section?.collapsible) sectionAttrs.push(`collapsible="true"`);
            lines.push(`${indent}<prm-section ${sectionAttrs.join(" ")}>`);
            currentSection = field.sectionTitle;
        } else if (!field.sectionTitle && currentSection !== null) {
            lines.push(`${indent}</prm-section>`);
            currentSection = null;
        }

        const fieldIndent = currentSection ? INDENT.repeat(depth + 1) : indent;
        lines.push(serializeField(field, fieldIndent));
    }

    // Close any open section
    if (currentSection !== null) {
        lines.push(`${indent}</prm-section>`);
    }

    return lines;
};

/**
 * Serialize a single field to its HTML tag.
 */
const serializeField = (field: BuilderFieldDef, indent: string): string => {
    const tag = getFieldTag(field.type);
    const attrs = buildFieldAttrs(field).map(([name, value]) => `${name}="${esc(value)}"`);

    if (field.type === "choice" && field.choiceItems?.length) {
        const opening = attrs.length <= 3
            ? `${indent}<${tag} ${attrs.join(" ")}>`
            : `${indent}<${tag}\n${attrs.map((a) => `${indent}${INDENT}${a}`).join("\n")}>`;
        const items = field.choiceItems.map((item) =>
            `${indent}${INDENT}<prm-choice-field-item label="${esc(item.label)}" value="${esc(item.value)}"/>`
        );
        return [opening, ...items, `${indent}</${tag}>`].join("\n");
    }

    if (attrs.length <= 3) {
        return `${indent}<${tag} ${attrs.join(" ")} />`;
    }

    return `${indent}<${tag}\n${attrs.map((a) => `${indent}${INDENT}${a}`).join("\n")}\n${indent}/>`;
};

const buildFieldAttrs = (field: BuilderFieldDef): Array<[string, string]> => {
    const attrs: Array<[string, string]> = [];

    if (field.uid) attrs.push(["prm:uid", field.uid]);

    attrs.push(["name", field.name]);
    if (field.label) attrs.push(["label", field.label]);
    if (field.required) attrs.push(["required", "true"]);
    if (field.placeholder) attrs.push(["placeholder", field.placeholder]);
    if (field.minLength) attrs.push(["minlength", field.minLength]);
    if (field.maxLength) attrs.push(["maxlength", field.maxLength]);
    if (field.type === "password" && field.passwordPattern) attrs.push(["pattern", field.passwordPattern]);
    if (field.type === "currency" && field.currencyCode) attrs.push(["prm:currency-code", field.currencyCode]);
    if (field.type === "currency" && field.currencyField) attrs.push(["prm:currency-field", field.currencyField]);
    if (field.type === "attachment" && field.accept) attrs.push(["accept", field.accept]);
    if (field.type === "attachment" && field.multiple) attrs.push(["multiple", "multiple"]);
    if ((field.type === "boolean" || field.type === "notice") && field.variant) attrs.push(["prm:variant", field.variant]);
    if ((field.type === "date" || field.type === "datetime" || field.type === "time") && field.minDate) attrs.push(["min", field.minDate]);
    if ((field.type === "date" || field.type === "datetime" || field.type === "time") && field.maxDate) attrs.push(["max", field.maxDate]);
    if (field.options && field.type !== "choice") attrs.push(["options", field.options]);
    if (field.map) attrs.push(["prm:map", field.map]);
    if (field.source) attrs.push(["prm:source", field.source]);
    if (field.display) attrs.push(["prm:display", field.display]);
    if (field.valueField) attrs.push(["prm:value-field", field.valueField]);
    if (field.referenceFilter) attrs.push(["prm:reference-filter", typeof field.referenceFilter === "string" ? field.referenceFilter : JSON.stringify(field.referenceFilter)]);
    if (field.defaultValue) attrs.push(["prm:default", field.defaultValue]);
    if (field.rules.visible) attrs.push(["prm:rule:visible", field.rules.visible]);
    if (field.rules.required) attrs.push(["prm:rule:required", field.rules.required]);
    if (field.rules.readonly) attrs.push(["prm:rule:readonly", field.rules.readonly]);
    if (field.rules.hidden) attrs.push(["prm:rule:hidden", field.rules.hidden]);
    if (field.rules.disabled) attrs.push(["prm:rule:disabled", field.rules.disabled]);
    if (field.rules.label) attrs.push(["prm:rule:label", field.rules.label]);
    if (field.className) attrs.push(["class", field.className]);
    if (field.style) attrs.push(["style", field.style]);

    return attrs;
};

const serializeNode = (node: Node, depth: number): string => {
    const indent = INDENT.repeat(depth);

    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        return text ? indent + escText(text) : "";
    }

    if (node.nodeType === Node.COMMENT_NODE) {
        return `${indent}<!--${node.textContent || ""}-->`;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const attrs = Array.from(el.attributes).map((attr) => `${attr.name}="${esc(attr.value)}"`).join(" ");
    const open = attrs ? `<${tag} ${attrs}` : `<${tag}`;
    const children = Array.from(el.childNodes).map((child) => serializeNode(child, depth + 1)).filter(Boolean);

    if (!children.length) {
        if (tag.startsWith("prm-") || VOID_TAGS.has(tag)) return `${indent}${open} />`;
        return `${indent}${open}></${tag}>`;
    }

    return [`${indent}${open}>`, ...children, `${indent}</${tag}>`].join("\n");
};

const normalizePrmSelfClosingTags = (html: string): string =>
    String(html || "").replace(/<(prm-[a-z0-9-]+)([^>]*)\/>/gi, "<$1$2></$1>");

const wrapInBody = (html: string): string => {
    if (html.includes("<body")) return html;
    return `<!DOCTYPE html><html><body>${html}</body></html>`;
};

/** Escape attribute values */
const esc = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");


const escText = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
